import type { TFunction } from 'i18next';

import type { IGoal } from '@feathr/blackbox';
import { EGoalType } from '@feathr/blackbox';

export interface IGoalTypeOption {
  id: EGoalType;
  name: string;
  description: string;
}

/**
 * Returns the goal attributes to apply when the user picks a new goal type.
 *
 * Conversion-pixel-trigger goals capture their value from the customer's
 * site code at fire time, so we reset conv_value to 0 instead of the
 * default 5 used for value-bearing goal types.
 *
 * @param newType The goal type the user just selected
 * @returns Partial goal attributes ready for `goal.set(...)`
 */
export function getGoalTypeChangeAttributes(
  newType: EGoalType,
): Pick<IGoal, 'conv_type' | 'segment' | 'multi_conversions' | 'conv_value'> {
  return {
    conv_type: newType,
    segment: undefined,
    multi_conversions: false,
    conv_value: newType === EGoalType.conversion_pixel ? 0 : 5,
  };
}

export const getGoalTypeOptions = (t: TFunction): IGoalTypeOption[] => [
  {
    id: EGoalType.page_view,
    name: t('URL visit'),
    description: t('Track when a person visits a specific URL'),
  },
  {
    id: EGoalType.group_entry,
    name: t('Group entry'),
    description: t('Track when a person enters a specific group'),
  },
  {
    id: EGoalType.conversion_pixel,
    name: t('Conversion pixel trigger'),
    description: t('Track when a conversion pixel is triggered'),
  },
  {
    id: EGoalType.form_submission,
    name: t('Feathr form submission'),
    description: t('Track when a person submits a Feathr-hosted form'),
  },
  {
    id: EGoalType.donation_form_submission,
    name: t('Feathr donation form submission'),
    description: t('Track when a person submits a Feathr Fundraising donation form'),
  },
];

/** Default goal names set on creation — safe to auto-update when goal type changes. */
const DEFAULT_GOAL_NAMES = ['New campaign goal', 'New flight goal'];

export function getAutoGoalName(type: EGoalType, t: TFunction): string {
  const option = getGoalTypeOptions(t).find((opt) => opt.id === type);
  return option ? `${option.name} goal` : '';
}

export function isAutoGoalName(name: string | undefined, t: TFunction): boolean {
  if (!name?.trim()) {
    return true;
  }
  const typeNames = getGoalTypeOptions(t).map((opt) => `${opt.name} goal`);
  return DEFAULT_GOAL_NAMES.includes(name) || typeNames.includes(name);
}
